/**
 * useMarkets.ts
 *
 * Higher-level hook that wraps usePredictionMarket with additional
 * UI-friendly state: filtering, search, and per-market loading indicators.
 */

import { useState, useEffect, useCallback } from 'react';
import { usePredictionMarket, Market } from './usePredictionMarket';

export type MarketFilter = 'all' | 'active' | 'expired' | 'resolved';

export interface UseMarketsResult {
  markets:        Market[];
  filteredMarkets: Market[];
  loadingMarkets: boolean;
  filter:         MarketFilter;
  searchQuery:    string;
  setFilter:      (f: MarketFilter) => void;
  setSearchQuery: (q: string) => void;
  refetch:        () => void;
  tx:             ReturnType<typeof usePredictionMarket>['tx'];
  resetTx:        () => void;
  createMarket:   (question: string, deadlineTimestamp: number) => Promise<boolean>;
  placeBet:       (marketId: number, prediction: boolean, amountEth: string) => Promise<boolean>;
  resolveMarket:  (marketId: number, outcome: boolean) => Promise<boolean>;
  claimWinnings:  (marketId: number) => Promise<boolean>;
}

export function useMarkets(): UseMarketsResult {
  const {
    markets,
    loadingMarkets,
    fetchMarkets,
    createMarket,
    placeBet,
    resolveMarket,
    claimWinnings,
    tx,
    resetTx,
  } = usePredictionMarket();

  const [filter, setFilter]           = useState<MarketFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchMarkets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refetch = useCallback(() => fetchMarkets(), [fetchMarkets]);

  const filteredMarkets = markets.filter(market => {
    const now = Date.now() / 1000;

    // Filter by status
    if (filter === 'active'   && (market.resolved || (market.deadline > 0 && now >= market.deadline))) return false;
    if (filter === 'expired'  && !(market.deadline > 0 && now >= market.deadline && !market.resolved)) return false;
    if (filter === 'resolved' && !market.resolved) return false;

    // Filter by search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      if (!market.question.toLowerCase().includes(q)) return false;
    }

    return true;
  });

  return {
    markets,
    filteredMarkets,
    loadingMarkets,
    filter,
    searchQuery,
    setFilter,
    setSearchQuery,
    refetch,
    tx,
    resetTx,
    createMarket,
    placeBet,
    resolveMarket,
    claimWinnings,
  };
}
